import React from 'react';
import { useState, useRef, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, Button, Popover, PopoverBody } from 'reactstrap';
import {
  useTranslator,
  useAppContext,
  useToast,
  callNativeMobileApp,
} from '@front-activerse/common-components';
import { HashimotoTestMeasurementFieldEnumToRemove } from '../types';
import { useNavigate } from 'react-router-dom';

interface ITestsPopover {
  removeMeasurement: any;
  measurement: any;
}

const TestsPopover: React.FC<ITestsPopover> = ({ removeMeasurement, measurement }) => {
  const { t } = useTranslator();
  const { toastError, toastSuccess } = useToast();
  const navigate = useNavigate();

  const {
    images: { optionsIcon, editIcon, deleteIcon },
  } = useAppContext();

  const [popoverOpen, setPopoverOpen] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const popoverRef = useRef(null);

  const handleOutsideClick = (e: MouseEvent) => {
    if (popoverRef.current && !(popoverRef.current as any).contains(e.target)) {
      setPopoverOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleOutsideClick);

    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, []);

  const handleDeleteModal = () => {
    togglePopover();
    setShowDeleteModal(true);
  };

  const togglePopover = () => {
    setPopoverOpen((prev) => !prev);
  };

  const handleEditMeasurement = () => {
    togglePopover();
    navigate(`/lab-tests/edit`, { state: { measurement } });
  };

  const toggleDeleteModal = () => {
    setShowDeleteModal(!showDeleteModal);
  };

  const handleDeleteMeasuremenet = async () => {
    toggleDeleteModal();

    const testsToRemove =
      HashimotoTestMeasurementFieldEnumToRemove[
        measurement.type as keyof typeof HashimotoTestMeasurementFieldEnumToRemove
      ];

    try {
      const res = await removeMeasurement.removeMeasurement({ id: measurement.id, testsToRemove });

      if (res.data.hashimoto.hashimotoTestRemove.__typename === 'BasicMutationSuccess') {
        callNativeMobileApp({
          action: 'refresh_data',
          payload: {
            module: 'tests',
          },
        });
        toastSuccess(<p>{t('labTests.notification.deletedConfirmation')}</p>);
      } else {
        toastError();
      }
    } catch (err) {
      toastError();
    }
  };

  return (
    <>
      <img
        src={optionsIcon}
        className="ms-2 cursor-pointer"
        style={{ marginTop: '-10px' }}
        onClick={togglePopover}
        id={`Popover${measurement.id}-${measurement.type}`}
        alt=""
      />

      <Popover
        placement="bottom-end"
        isOpen={popoverOpen}
        target={`Popover${measurement.id}-${measurement.type}`}
        toggle={togglePopover}
        innerRef={popoverRef}
      >
        <PopoverBody>
          <Button
            color="outline"
            onClick={handleEditMeasurement}
            className="d-flex align-items-center p-0 w-100"
          >
            <img src={editIcon} className="me-2" alt="contextualMenu.edit" />
            {t('contextualMenu.edit')}
          </Button>
          <hr className="my-m stroke-light" />
          <Button
            color="outline"
            className="d-flex align-items-center p-0 text-text-error-color w-100"
            onClick={handleDeleteModal}
          >
            <img src={deleteIcon} className="me-2" alt="contextualMenu.delete" />
            {t('contextualMenu.delete')}
          </Button>
        </PopoverBody>
      </Popover>

      <Modal
        isOpen={showDeleteModal}
        toggle={toggleDeleteModal}
        centered
        size="md"
        returnFocusAfterClose={false}
      >
        <ModalHeader toggle={toggleDeleteModal} />
        <ModalBody className="pt-0">
          <h3 className="title-small semi-bold text-center">{t('labTests.delete.confirmTitle')}</h3>
          <p className="text-center">{t('labTests.delete.confirmDescription')}</p>
          <div className="d-flex my-3">
            <Button color="secondary" outline className="me-1 w-50" onClick={toggleDeleteModal}>
              {t('labTests.cancelButton')}
            </Button>
            <Button color="primary" className="ms-1 w-50" onClick={handleDeleteMeasuremenet}>
              {t('labTests.deleteButton')}
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
};

export default TestsPopover;
